import { Component, inject, OnInit, OnDestroy, signal, effect, computed, input, untracked, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import * as d3 from 'd3';
import { CommonModule } from '@angular/common';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DashboardLayoutEditorComponent } from '../../../../shared/components/dashboard/dashboard-layout-editor.component';
import { ApiService } from '../../../../core/services/api.service';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import {
  DashboardPageKey,
  PractinaDashboardSectionId,
  PractinaDashboardSectionDef,
} from '../../../../core/enums/dashboard-cards.enum';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilterByWidgetKeyMap, findCatalogPageId, resolveActiveCatalogProductId } from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  getLiveSectionColClass,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { DatePickerComponent, DateRangePickerOutput, DateRangePickerValue, TimeUnit } from '../../../../shared/components/date-picker/date-picker.component';
import { LineAreaChartComponent, ILineAreaSeries } from '../../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { CylinderChartComponent, CylinderChartData } from '../../../../shared/components/charts/bar-charts/cylinder-chart/cylinder-chart.component';
import { FunnelChartComponent, IFunnelDataPoint } from '../../../../shared/components/charts/funnel-chart/funnel-chart.component';
import { IsometricBarComponent, IsometricBarData } from '../../../../shared/components/charts/isometric-bar/isometric-bar.component';
import { MultipleBarComponent, BarChartData } from '../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { PieChartComponent } from '../../../../shared/components/charts/pie-chart/pie-chart.component';
import { StackedBarComponent, StackedBarData } from '../../../../shared/components/charts/stacked-bar/stacked-bar.component';
import { TooltipComponent } from '../../../../shared/components/ui/tooltip/tooltip.component';
import { PrismService, PrismResponse } from '../../../../core/services/prism.service';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged } from 'rxjs/operators';
import {
  DashboardCard,
  ACQUISITION_CARDS,
  CONVERSION_CARDS,
  SUBSCRIPTION_CARDS,
  MONTHLY_SIGNUP_TREND_CONFIG,
  CONVERSION_TREND_CONFIG,
  RENEWAL_TREND_CONFIG,
  AI_CONTENT_TREND_CONFIG,
  AD_SPEND_TREND_CONFIG,
  MARKETING_PERFORMANCE_METRICS_DEFAULT,
  EXECUTIVE_PERFORMANCE_METRICS_DEFAULT
} from './practina-dashboard.constants';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: PractinaDashboardSectionId;
  visible: boolean;
  colSpan: number;
  order: number;
}

interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

@Component({
  selector: 'app-practina-dashboard',
  standalone: true,
  imports: [CommonModule, DashboardLayoutEditorComponent, IconComponentV2, DatePickerComponent, LineAreaChartComponent, CylinderChartComponent, FunnelChartComponent, IsometricBarComponent, MultipleBarComponent, PieChartComponent, StackedBarComponent, IndividualToasterComponent, TooltipComponent],
  templateUrl: './practina-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
      display: block;
      height: 100%;
    }
  `,
})
export class PractinaDashboardComponent implements OnInit, OnDestroy {
  private prismService = inject(PrismService);
  private breakpointObserver = inject(BreakpointObserver);
  private apiService = inject(ApiService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private cdr = inject(ChangeDetectorRef);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.PRACTINA_DASHBOARD;
  readonly SectionId = PractinaDashboardSectionId;
  readonly SECTIONS = PractinaDashboardSectionDef;
  currentTimeUnit: TimeUnit = 'month';
  initialDateValue: DateRangePickerValue = {
    startDate: dayjs.utc().tz(LA_TIMEZONE).subtract(89, 'day').startOf('day').toISOString(),
    endDate: dayjs.utc().tz(LA_TIMEZONE).endOf('day').toISOString(),
    unit: 'month',
    comparisonType: 'none'
  };
  durationDays = signal<number>(89);

  // Per-tile loading flags (true = loading skeleton shown)
  loadingCards: boolean = false;           // acquisition / conversion / subscription cards
  loadingSignupTrend: boolean = false;     // totalSignupVsSocialSignups
  loadingProfileCompletion: boolean = false; // OnboardingVsProfile
  loadingFunnel: boolean = false;          // revenueJourney
  loadingConversionTrend: boolean = false; // TrialVsPaidVsFreemium
  loadingConversionPipeline: boolean = false; // conversionPipeline
  loadingRenewalTrend: boolean = false;    // autoRenewVsFailure
  loadingAiContent: boolean = false;       // allPostVsAiPost
  loadingPlatformPosts: boolean = false;   // postsByPlatforms
  loadingDemoSignup: boolean = false;      // demoBookedVsRegistration
  loadingDealRealization: boolean = false; // dealCreatedVsDealClaimed
  loadingAdLeads: boolean = false;         // ad leads / campaign (static for now)
  loadingAdSpend: boolean = false;         // ad spend by channel
  loadingMarketingOverview: boolean = false;
  loadingExecutiveSummary: boolean = false;

  // Data availability flags — all false until live data arrives
  hasMonthlySignupTrendData: boolean = false;
  hasProfileCompletionData: boolean = false;
  hasSignupToRevenueFunnelData: boolean = false;
  hasConversionTrendData: boolean = false;
  hasConversionPipelineData: boolean = false;
  hasRenewalTrendData: boolean = false;
  hasAiContentTrendData: boolean = false;
  hasAdLeadsCampaignData: boolean = false;
  hasAdSpendData: boolean = false;
  hasPlatformPostDistributionData: boolean = false;
  hasDemoSignupData: boolean = false;
  hasDealRealizationData: boolean = false;
  private comparisonValues = signal<Record<string, number>>({});
  private currentValues = signal<Record<string, number>>({});
  private activeSubscriptions = 0;
  private activeDataSubscription?: Subscription;

  formatTick = (date: any): string => {
    switch (this.currentTimeUnit) {
      case 'day':
      case 'week':
        return d3.utcFormat('%d %b')(date);
      case 'month':
        return d3.utcFormat('%b')(date);
      case 'year':
        return d3.utcFormat('%Y')(date);
      default:
        return d3.utcFormat('%b')(date);
    }
  };
  centerValue: any;
  pieChartHeight: number = 300;

  ngOnInit(): void {
    this._initLayout();
    if (this.mixedMode()) {
      this._loadInitialDateRange();
    }
  }

  ngOnDestroy(): void {
    if (this.activeDataSubscription) {
      this.activeDataSubscription.unsubscribe();
    }
  }

  constructor() {
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      // Track both signals so we re-resolve once `selectedProductPid`
      // hydrates (common on first paint). Resolving with only the cuid
      // `selectedProductId` scopes the catalog lookup to the wrong
      // product and can cache a cross-product `pages.id` that has no
      // `pr_an_*` widgets — breaking Customize Layout Apply.
      const productId = this.apiService.selectedProductId();
      const productPid = this.apiService.selectedProductPid();
      if (!productId && productPid == null) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<PractinaDashboardSectionId>();
          keys.forEach((k) => {
            // Try the reverse map first so backend `pr_an_*` widget
            // keys hydrate against the matching `pd_*` section id.
            // Falls through to the legacy direct-match for any backend
            // key that already uses the section enum value (e.g. a
            // future rename or an entry added without a mapping yet).
            const mapped = this.WIDGET_KEY_TO_SECTION.get(k);
            if (mapped != null) {
              pinned.add(mapped);
              return;
            }
            if (Object.values(PractinaDashboardSectionId).includes(k as PractinaDashboardSectionId)) {
              pinned.add(k as PractinaDashboardSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<PractinaDashboardSectionId, number>();
            for (const sec of Object.values(PractinaDashboardSectionId)) {
              // Look up the catalog `widgets.id` using the backend
              // `widget_key` from {@link SECTION_WIDGET_KEYS} first.
              // Falls back to the raw `pd_*` section id so any future
              // section that gets renamed on the backend (and is added
              // to the map) keeps working without a frontend redeploy.
              const mappedKey = this.SECTION_WIDGET_KEYS[sec];
              const id =
                (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });

    effect(() => {
      const cur = this.currentValues();
      const prev = this.comparisonValues();
      this.updateAllMetrics(cur, prev);
    });

    this.breakpointObserver.observe(['(max-width: 1366px)'])
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((result) => {
        if (result.matches) {
          this.pieChartHeight = 250;
          this.cdr.markForCheck();
        }
      });

  }

  // ─── Section layout state ─────────────────────────────────────────────────
  readonly layoutMap = signal<Record<PractinaDashboardSectionId, SectionLayoutItem>>(
    {} as Record<PractinaDashboardSectionId, SectionLayoutItem>,
  );

  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  /** Whether a section should render (layout visibility + mixed-mode filter). */
  showSection(sectionId: PractinaDashboardSectionId): boolean {
    return this.embeddedVisibleSections().some((s) => s.id === sectionId);
  }

  sectionOrder(sectionId: PractinaDashboardSectionId): number {
    return this.layoutMap()[sectionId]?.order ?? 0;
  }

  /** Sort key for a dashboard row that contains one or more sections. */
  rowDisplayOrder(sectionIds: PractinaDashboardSectionId[]): number {
    const orders = sectionIds
      .filter((id) => this.showSection(id))
      .map((id) => this.sectionOrder(id));
    return orders.length ? Math.min(...orders) : 9999;
  }

  /** Original pair-row grid: one chart = full width; two = side-by-side at xl. */
  chartPairGridClass(sectionIds: PractinaDashboardSectionId[], gap: 'gap-3' | 'gap-4' = 'gap-4'): string {
    const visibleCount = sectionIds.filter((id) => this.showSection(id)).length;
    const gapClass = gap === 'gap-3' ? 'gap-3 [@media(max-height:620px)]:gap-2' : 'gap-4 [@media(max-height:620px)]:gap-2';
    return visibleCount <= 1
      ? `grid grid-cols-1 ${gapClass}`
      : `grid grid-cols-1 xl:grid-cols-2 ${gapClass}`;
  }

  chartPairGridClassLg(sectionIds: PractinaDashboardSectionId[]): string {
    const visibleCount = sectionIds.filter((id) => this.showSection(id)).length;
    return visibleCount <= 1
      ? 'grid grid-cols-1 gap-2'
      : 'grid grid-cols-1 lg:grid-cols-2 gap-2';
  }

  getSectionColClass(item: SectionLayoutItem | PractinaDashboardSectionId): string {
    if (this.mixedMode()) return 'col-span-12';
    const id = typeof item === 'string' ? item : item.id;
    const span =
      this.layoutMap()[id]?.colSpan ??
      this.DEFAULT_COL_SPAN[id as PractinaDashboardSectionId] ??
      12;
    return getLiveSectionColClass(span);
  }

  /** Chart/metric card shell — drops duplicate chrome when embedded in My Dashboard. */
  chartSectionShellClass(tall = false): string {
    if (this.mixedMode()) {
      return 'h-full flex flex-col min-h-0 overflow-hidden';
    }
    if (tall) {
      return 'bg-white dark:bg-surface-2 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm overflow-hidden flex flex-col min-h-[380px] xl:h-[550px] [@media(max-height:700px)]:h-[450px] [@media(max-height:620px)]:h-[450px]';
    }
    return 'bg-white dark:bg-surface-2 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm overflow-hidden flex flex-col min-h-[350px] xl:h-[450px] [@media(max-height:700px)]:h-[380px] [@media(max-height:620px)]:h-[380px]';
  }

  metricSectionShellClass(): string {
    return this.mixedMode()
      ? 'h-full flex flex-col min-h-0 overflow-hidden'
      : 'bg-white dark:bg-surface-2 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm overflow-hidden flex flex-col h-[400px]';
  }

  overviewSectionShellClass(): string {
    return this.mixedMode()
      ? 'h-full flex flex-col min-h-0 overflow-hidden relative'
      : 'bg-white dark:bg-surface-2 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm flex flex-col relative overflow-hidden h-[450px]';
  }

  embeddedChartHeight(defaultHeight = 330): number {
    return this.mixedMode() ? 260 : defaultHeight;
  }

  embeddedCylinderWidth(): number {
    return this.mixedMode() ? 520 : 800;
  }

  /** KPI stat tile — keep a single border when embedded (parent supplies unified-tile-body). */
  kpiCardShellClass(): string {
    if (this.mixedMode()) {
      return 'flex flex-1 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-surface-2 py-4 px-3 relative h-[106px]';
    }
    return 'flex flex-1 bg-white dark:bg-surface-2 rounded-2xl [@media(max-height:620px)]:rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 py-4 [@media(max-height:700px)]:py-3 [@media(max-height:620px)]:py-1.5 px-3 hover:shadow-md transition-shadow relative h-[106px] [@media(max-height:700px)]:h-[86px] [@media(max-height:620px)]:h-[62px]';
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── Pin to Custom Dashboard ──────────────────────────────────────────────
  readonly pinnedSections = signal<Set<PractinaDashboardSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<PractinaDashboardSectionId>>(new Set());

  readonly pinnableWidgetIds = signal<Map<PractinaDashboardSectionId, number>>(
    new Map(),
  );

  /**
   * Frontend section id (`pd_*`) → backend catalog `widget_key`
   * (`pr_an_*`) mapping. The two enums diverged historically:
   *
   *   • Frontend section ids use a `pd_` prefix and human-readable
   *     suffixes (`pd_monthly_signup_trend`, `pd_profile_completion`,
   *     `pd_marketing_overview`, …).
   *   • Backend catalog under product Practina (`pages.id = 36`)
   *     registers widgets with `pr_an_` prefixes and slightly
   *     different suffixes (`pr_an_signup_trend`,
   *     `pr_an_onboarding_completion`,
   *     `pr_an_marketing_performance_overview`, …).
   *
   * Without this translation:
   *   • The fast path in `addToCustomDashboard` can never resolve a
   *     `widgets.id` from {@link pinnableWidgetIds} (lookup by
   *     section id misses every backend key).
   *   • The fallback `toggleSection` call sends `widgetKey: "pd_*"`
   *     which the backend rejects with
   *     "Widget X is not registered in the dashboards catalog".
   *   • The hydration effect can never light the green "Added" pill
   *     because the backend's `widget_key`s never match the section
   *     enum values.
   *
   * Keep this map in sync with the backend catalog for
   * `pages.id = 36` (see Analytical-dashboard-widgets page).
   */
  private readonly SECTION_WIDGET_KEYS: Partial<Record<PractinaDashboardSectionId, string>> = {
    [PractinaDashboardSectionId.ACQUISITION]: 'pr_an_acquisition_kpis',
    [PractinaDashboardSectionId.CONVERSION]: 'pr_an_conversion_kpis',
    [PractinaDashboardSectionId.SUBSCRIPTIONS]: 'pr_an_subscriptions_summary',
    [PractinaDashboardSectionId.MONTHLY_SIGNUP_TREND]: 'pr_an_signup_trend',
    [PractinaDashboardSectionId.PROFILE_COMPLETION]: 'pr_an_onboarding_completion',
    [PractinaDashboardSectionId.SIGNUP_REVENUE_FUNNEL]: 'pr_an_revenue_journey',
    [PractinaDashboardSectionId.CONVERSION_MONETIZATION]: 'pr_an_user_conversion_monetization_trend',
    [PractinaDashboardSectionId.CONVERSION_PIPELINE]: 'pr_an_conversion_pipeline',
    [PractinaDashboardSectionId.RENEWAL_TREND]: 'pr_an_renewal_payment_stability',
    [PractinaDashboardSectionId.AI_CONTENT_TREND]: 'pr_an_ai_content_trend',
    [PractinaDashboardSectionId.AD_LEADS_CAMPAIGN]: 'pr_an_ad_leads_campaign_activity',
    [PractinaDashboardSectionId.PLATFORM_POST_DISTRIBUTION]: 'pr_an_user_content_distribution',
    [PractinaDashboardSectionId.AD_SPEND_CHANNEL]: 'pr_an_ad_spend_channel',
    [PractinaDashboardSectionId.DEMO_SIGNUP_TREND]: 'pr_an_demo_signup_trend',
    [PractinaDashboardSectionId.DEAL_REALIZATION]: 'pr_an_deal_realization',
    [PractinaDashboardSectionId.MARKETING_OVERVIEW]: 'pr_an_marketing_performance_overview',
    [PractinaDashboardSectionId.EXECUTIVE_SUMMARY]: 'pr_an_executive_performance_summary',
  };

  /**
   * Reverse lookup: backend `widget_key` → frontend section id.
   * Built once from {@link SECTION_WIDGET_KEYS}; used by the hydration
   * effect so the green "Added" pill renders against the correct
   * section after a page refresh.
   */
  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<string, PractinaDashboardSectionId> =
    new Map(
      (Object.entries(this.SECTION_WIDGET_KEYS) as [
        PractinaDashboardSectionId,
        string,
      ][]).map(([sec, key]) => [key, sec]),
    );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  isAddedToCustomDashboard(sectionId: PractinaDashboardSectionId): boolean {
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(sectionId: PractinaDashboardSectionId): boolean {
    return this.pendingPinSections().has(sectionId);
  }
  addToCustomDashboard(sectionId: PractinaDashboardSectionId): void {
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);
    const productId = resolveActiveCatalogProductId(this.apiService);
    if (productId == null) return;

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };


    const fire = (resolvedPageId: number) => {
      const widgetId = this.pinnableWidgetIds().get(sectionId);
      if (widgetId != null) {
        this.customDashboardPin.toggleLayoutItem({
          pageId: resolvedPageId,
          widgetId,
          sourcePageId: resolvedPageId,
          colSpan,
          rowSpan: 1,
          customConfig: {},
          productId,
          isCurrentlyPinned,
          onPinnedChange,
          onPendingChange,
        });
        return;
      }

      // Translate the frontend section id to the backend `widget_key`
      // before falling back to `toggleSection`. Sending the raw `pd_*`
      // id would trigger the backend's "Widget X is not registered in
      // the dashboards catalog" toast. Sections without a mapping
      // still send the raw id so future additions don't silently drop.
      this.customDashboardPin.toggleSection({
        pageId: resolvedPageId,
        widgetKey: this.SECTION_WIDGET_KEYS[sectionId] ?? sectionId,
        colSpan,
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
    };

    // Fast path — catalog `pages.id` already cached by the constructor
    // effect. Fires the API immediately, no additional round-trips.
    const cachedPageId = this.effectiveCatalogPageId();
    if (cachedPageId != null) {
      fire(cachedPageId);
      return;
    }

    // Fallback / slow path — pageId not yet resolved. Resolve the
    // catalog on-click so the API still fires.
    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId = findCatalogPageId(
          catalog as any[],
          this.PAGE_KEY,
          productId,
        );

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        // Cache so the fast path kicks in on the next click and the
        // existing hydration effect for "Added" pills stays consistent.
        this.effectiveCatalogPageId.set(resolvedPageId);

        // Resolve `widgets.id` map for this page so this click can
        // use the fast `toggleLayoutItem` variant. When the map
        // already has an entry from a prior effect run, this is
        // essentially free.
        if (this.pinnableWidgetIds().get(sectionId) == null) {
          this.customDashboardPin
            .resolveCatalogWidgetIdsForPage(resolvedPageId, productId)
            .subscribe((widgetIdMap) => {
              const next = new Map(this.pinnableWidgetIds());
              for (const id of Object.values(PractinaDashboardSectionId)) {
                // Mirror the constructor-effect lookup: try the
                // backend `widget_key` from {@link SECTION_WIDGET_KEYS}
                // first, then fall back to the raw `pd_*` id.
                const mappedKey = this.SECTION_WIDGET_KEYS[id];
                const wid =
                  (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                  widgetIdMap.get(id);
                if (wid != null) next.set(id, wid);
              }
              this.pinnableWidgetIds.set(next);
              fire(resolvedPageId);
            });
          return;
        }

        fire(resolvedPageId);
      });
  }


  private _warnCatalogMiss(
    catalog: any[],
    productId: string | number | null,
  ): void {
    const scopedProducts =
      productId != null && String(productId).trim() !== ''
        ? (catalog ?? []).filter(
          (p: any) => String(p?.id) === String(productId),
        )
        : (catalog ?? []);

    const productsForLog =
      scopedProducts.length > 0 ? scopedProducts : (catalog ?? []);

    const pageNames: string[] = [];
    for (const product of productsForLog) {
      for (const page of product?.pages ?? []) {
        const name = String(page?.name ?? '').trim();
        if (name) pageNames.push(name);
      }
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[PractinaDashboard] No catalog page found for ` +
      `"${this.PAGE_KEY}" (active product=${productId}). ` +
      `Catalog advertised pages for this product: ` +
      `${pageNames.length ? pageNames.join(', ') : '(none)'}. ` +
      `If the correct page is in the list above, add a matching ` +
      `substring to CATALOG_PAGE_HINTS[PRACTINA_DASHBOARD] ` +
      `in dashboard-embed.helpers.ts. If the list is empty, the ` +
      `backend has no catalog page for this report yet.`,
    );
  }

  // ─── Customize Layout editor ──────────────────────────────────────────────
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  private _layoutSnapshot: Record<PractinaDashboardSectionId, SectionLayoutItem> | null =
    null;

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];
  private readonly LAYOUT_LS_KEY = 'prism_practina_dashboard_layout_v2';

  /**
   * Default widths used by the live dashboard (when no localStorage
   * entry exists) AND by the "Set Default" / Reset action in the
   * Customize Layout popup. Must mirror the visual baseline:
   *
   *   • Acquisition (2 KPIs)   → col-4  (Small)
   *   • Conversion (3 KPIs)    → col-8  (Large)  ← row 1 totals 12.
   *   • Subscriptions (5 KPIs) → col-12 (Full)   ← row 2.
   *
   * Keeping Conversion at 4 here caused Reset to shrink the card to
   * a sliver and break the 4+8 row split — the live layout only
   * looked correct because previous user customizations had cached
   * `colSpan=8` in localStorage.
   */
  private readonly DEFAULT_COL_SPAN: Record<PractinaDashboardSectionId, number> = {
    [PractinaDashboardSectionId.ACQUISITION]: 4,
    [PractinaDashboardSectionId.CONVERSION]: 8,
    [PractinaDashboardSectionId.SUBSCRIPTIONS]: 12,
    [PractinaDashboardSectionId.MONTHLY_SIGNUP_TREND]: 6,
    [PractinaDashboardSectionId.PROFILE_COMPLETION]: 6,
    [PractinaDashboardSectionId.SIGNUP_REVENUE_FUNNEL]: 6,
    [PractinaDashboardSectionId.CONVERSION_MONETIZATION]: 6,
    [PractinaDashboardSectionId.CONVERSION_PIPELINE]: 12,
    [PractinaDashboardSectionId.RENEWAL_TREND]: 6,
    [PractinaDashboardSectionId.AI_CONTENT_TREND]: 6,
    [PractinaDashboardSectionId.AD_LEADS_CAMPAIGN]: 12,
    [PractinaDashboardSectionId.PLATFORM_POST_DISTRIBUTION]: 6,
    [PractinaDashboardSectionId.AD_SPEND_CHANNEL]: 6,
    [PractinaDashboardSectionId.DEMO_SIGNUP_TREND]: 6,
    [PractinaDashboardSectionId.DEAL_REALIZATION]: 6,
    [PractinaDashboardSectionId.MARKETING_OVERVIEW]: 6,
    [PractinaDashboardSectionId.EXECUTIVE_SUMMARY]: 6,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
      defaultColSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as PractinaDashboardSectionId] ?? 12,
      ),
    }));
    this.editorItems.set(items);
    this._layoutSnapshot = JSON.parse(JSON.stringify(this.layoutMap()));
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.layoutMap.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: PractinaDashboardSectionId): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    const defaults: LayoutEditorItem[] = this.SECTIONS.map((s, idx) => {
      const defaultSpan = this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as PractinaDashboardSectionId] ?? 12,
      );
      return {
        id: s.id as PractinaDashboardSectionId,
        title: s.title,
        visible: true,
        colSpan: defaultSpan,
        order: idx,
        defaultColSpan: defaultSpan,
      };
    });
    this.editorItems.set(defaults);
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout = this._buildLayoutFromEditor();
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);
    this._layoutSnapshot = null;

    // Best-effort backend persistence — see {@link _persistLayoutToBackend}.
    // The local state above is unchanged regardless of whether the
    // network call succeeds, so existing functionality / UI is preserved.
    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard via the shared {@link CustomizeLayoutBatchService} —
   * same flow Campaign Ads Stats / Business Performance Report use.
   *
   * Two paths, picked dynamically per click so the API call always
   * fires regardless of whether the reactive catalog effects in the
   * constructor have hydrated yet:
   *
   *   1. **Fast path** — `effectiveCatalogPageId()` AND
   *      `pinnableWidgetIds()` are both populated (catalog already
   *      cached under `pages.id = 36`). Maps each draft row to its
   *      pre-resolved `widgets.id` and calls
   *      `applyEditorDraftToPageDashboard` directly.
   *
   *   2. **Slow / fallback path** — pageId and/or widget-id map not
   *      ready yet. Triggers `getCatalogProducts()` on demand,
   *      resolves the page id, caches it, then hands the draft to
   *      `applyEditorDraftToPageDashboardByKeys` with backend
   *      `widget_key`s from {@link SECTION_WIDGET_KEYS} (`pd_*` →
   *      `pr_an_*`). The service forkJoins the catalog + per-page
   *      dashboard load and resolves ids from whichever responds.
   *
   * Errors are silently swallowed by the service.
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    const toBackendKeysDraft = (resolvedPageId: number) =>
      draft.map((d, idx) => ({
        key: this.SECTION_WIDGET_KEYS[d.id] ?? String(d.id),
        sourcePageId: resolvedPageId,
        colSpan: this._normalizeColSpan(d.colSpan),
        visible: d.visible,
        customConfig: null,
        order: idx,
      }));

    // Fast path — numeric `widgets.id` already resolved from catalog.
    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d, idx) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    // Slow / fallback path — resolve `pageId` on demand and let the
    // by-keys variant resolve widget ids from the catalog and/or the
    // existing per-page dashboard payload.
    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId =
          cachedPageId ??
          findCatalogPageId(catalog as any[], this.PAGE_KEY, productId);

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        if (cachedPageId == null) {
          this.effectiveCatalogPageId.set(resolvedPageId);
        }

        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: resolvedPageId,
          productId,
          fallbackSourcePageId: resolvedPageId,
          draft: toBackendKeysDraft(resolvedPageId),
        });
      });
  }

  decreaseEditorCardColSpan(id: PractinaDashboardSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[i.id] ?? 12);
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: PractinaDashboardSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }


  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[item.id] ?? 12);
    return this.COL_SPAN_STEPS[idx - 1] >= minSpan;
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }

  private _buildLayoutFromEditor(): Record<PractinaDashboardSectionId, SectionLayoutItem> {
    return buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this.SECTIONS as readonly { id: PractinaDashboardSectionId }[],
      this.DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
  }

  private _syncLayoutFromEditor(): void {
    if (!this.isLayoutEditorOpen()) return;
    this.layoutMap.set(this._buildLayoutFromEditor());
  }

  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<PractinaDashboardSectionId, SectionLayoutItem> =
      {} as Record<PractinaDashboardSectionId, SectionLayoutItem>;
    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as PractinaDashboardSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });
    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<PractinaDashboardSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // ignore
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  private _loadInitialDateRange(): void {
    const startDate = dayjs.utc(this.initialDateValue.startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endDate = dayjs.utc(this.initialDateValue.endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    this.loadDashboardData(startDate, endDate, this.initialDateValue.unit, false);
  }

  private updateAllMetrics(cur: Record<string, number>, prev: Record<string, number>) {
    const metricMap: Record<string, { cards: DashboardCard[], index: number, isPercent?: boolean }> = {
      'totalSignup': { cards: this.acquisitionCards, index: 0 },
      'totalActiveAds': { cards: this.acquisitionCards, index: 1 },
      'freeToPaid': { cards: this.conversionCards, index: 0 },
      'trialToPaid': { cards: this.conversionCards, index: 1 },
      'trialToFreemium': { cards: this.conversionCards, index: 2 },
      'totalActivePaidSubscription': { cards: this.subscriptionCards, index: 0 },
      'newPaidSubscriber': { cards: this.subscriptionCards, index: 1 },
      'netGrowth': { cards: this.subscriptionCards, index: 2 },
      'cancellations': { cards: this.subscriptionCards, index: 3 },
      'churnRate': { cards: this.subscriptionCards, index: 4, isPercent: true }
    };

    Object.keys(metricMap).forEach(tile => {
      const config = metricMap[tile];
      const curVal = cur[tile];
      const prevVal = prev[tile];
      const card = config.cards[config.index];

      if (curVal !== undefined) {
        const formattedValue = config.isPercent ? `${curVal.toFixed(2)}%` : this.formatNumber(curVal);
        card.value = formattedValue;
        card.loading = false;
      }

      // Only calculate trend if it wasn't already set manually (e.g., from prevWindow)
      // or if we have explicit comparison data
      if (prevVal !== undefined && curVal !== undefined) {
        if (prevVal > 0) {
          const diff = curVal - prevVal;
          const percent = (diff / prevVal) * 100;
          card.trend = {
            value: `${Math.abs(percent).toFixed(1)}%`,
            direction: diff > 0 ? 'up' : (diff < 0 ? 'down' : 'none'),
            label: 'vs prev period',
            textColorClass: diff > 0 ? 'text-emerald-500' : (diff < 0 ? 'text-rose-500' : 'text-gray-400')
          };
        } else if (curVal > 0 && prevVal === 0) {
          card.trend = {
            value: '100%',
            direction: 'up',
            label: 'vs prev period',
            textColorClass: 'text-emerald-500'
          };
        } else {
          card.trend = undefined;
        }
      }
    });

    this.cdr.markForCheck();
  }

  onDateRangeChange(event: DateRangePickerOutput): void {
    this.currentTimeUnit = event.unit;
    const startDate = dayjs.utc(event.start_date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endDate = dayjs.utc(event.end_date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    this.durationDays.set(dayjs(endDate).diff(dayjs(startDate), 'day'));
    this.loadDashboardData(startDate, endDate, event.unit, false);
  }

  loadDashboardData(startTime: string, endTime: string, unit: string, isComparison: boolean = false): void {
    if (!isComparison) {
      if (this.activeDataSubscription) {
        this.activeDataSubscription.unsubscribe();
        this.activeDataSubscription = undefined;
      }
    }

    this.activeSubscriptions++;
    this.setLoaders(true);

    if (!isComparison) {
      this.loadingCards = true;
      this.acquisitionCards.forEach(c => c.loading = true);
      this.conversionCards.forEach(c => c.loading = true);
      this.subscriptionCards.forEach(c => c.loading = true);
      this.currentValues.set({});
      this.comparisonValues.set({});
      this.hasMonthlySignupTrendData = false;
      this.hasProfileCompletionData = false;
      this.hasSignupToRevenueFunnelData = false;
      this.hasConversionTrendData = false;
      this.hasConversionPipelineData = false;
      this.hasRenewalTrendData = false;
      this.hasAiContentTrendData = false;
      this.hasAdLeadsCampaignData = false;
      this.hasAdSpendData = false;
      this.hasPlatformPostDistributionData = false;
      this.hasDemoSignupData = false;
      this.hasDealRealizationData = false;
    }

    this.cdr.markForCheck();

    const sub = this.prismService.getPrismDataStream(startTime, endTime, unit).subscribe({
      next: (response: PrismResponse) => {
        this.mapResponseToChart(response, response.query?.unit || unit, isComparison);
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        console.error('Failed to load dashboard data', err);
        if (!isComparison && this.activeDataSubscription === sub) {
          this.activeDataSubscription = undefined;
        }
        this.activeSubscriptions--;
        if (this.activeSubscriptions <= 0) {
          this.activeSubscriptions = 0;
          this.clearAllLoaders();
        }
        this.cdr.markForCheck();
      },
      complete: () => {
        if (!isComparison && this.activeDataSubscription === sub) {
          this.activeDataSubscription = undefined;
        }
        this.activeSubscriptions--;
        if (this.activeSubscriptions <= 0) {
          this.activeSubscriptions = 0;
          this.clearAllLoaders();
        }
        this.cdr.markForCheck();
      }
    });

    if (!isComparison) {
      this.activeDataSubscription = sub;
    }
  }

  private setLoaders(state: boolean): void {
    const loaders = {
      loadingCards: state,
      loadingSignupTrend: state,
      loadingProfileCompletion: state,
      loadingFunnel: state,
      loadingConversionTrend: state,
      loadingConversionPipeline: state,
      loadingRenewalTrend: state,
      loadingAiContent: state,
      loadingPlatformPosts: state,
      loadingDemoSignup: state,
      loadingDealRealization: state,
      loadingAdLeads: state,
      loadingAdSpend: state,
      loadingMarketingOverview: state,
      loadingExecutiveSummary: state
    };
    Object.assign(this, loaders);
  }

  private clearAllLoaders(): void {
    this.setLoaders(false);
  }

  private mapResponseToChart(response: PrismResponse, unit: string, isComparison: boolean = false): void {
    const { tile, data } = response;

    if (isComparison) {
      if (typeof data === 'number' || !isNaN(Number(data))) {
        this.comparisonValues.update(prev => ({ ...prev, [tile]: Number(data) }));
      }
      return;
    }

    // Handlers for complex objects that update charts or specific cards
    const handlers: Record<string, (d: any, u: string) => void> = {
      'OnboardingVsProfile': (d, u) => this.mapOnboardingVsProfile(d, u),
      'TrialVsPaidVsFreemium': (d) => this.mapTrialVsPaidVsFreemium(d),
      'autoRenewVsFailure': (d) => this.mapAutoRenewVsFailure(d),
      'allPostVsAiPost': (d) => this.mapAllPostVsAiPost(d),
      'postsByPlatforms': (d) => this.mapPostsByPlatforms(d),
      'demoBookedVsRegistration': (d, u) => this.mapDemoBookedVsRegistration(d, u),
      'dealCreatedVsDealClaimed': (d, u) => this.mapDealCreatedVsDealClaimed(d, u),
      'totalSignupVsSocialSignups': (d) => this.mapTotalSignupVsSocialSignups(d),
      'conversionPipeline': (d, u) => this.mapConversionPipeline(d, u),
      'revenueJourney': (d) => this.mapRevenueJourney(d),
      'addLeadVsActiveAdsVsCampaignsCreated': (d, u) => this.mapAdLeadVsActiveAdsVsCampaignsCreated(d, u),
      'marketingPerformanceOverview': (d) => this.mapMarketingPerformanceOverview(d),
      'conversion': (d) => this.mapConversion(d),
      'executivePerformanceSummary': (d) => this.mapExecutivePerformanceSummary(d),
      'adSpendsByChannel': (d, u) => this.mapAdSpendsByChannel(d, u)
    };

    if (handlers[tile]) {
      handlers[tile](data, unit);
      return;
    }

    // Special handling for metrics that now return objects with inline trend data
    const metricMapping: Record<string, { card: DashboardCard, valKey: string, trendKey: string }> = {
      'totalSignup': { card: this.acquisitionCards[0], valKey: 'totalSignup', trendKey: 'prevWindowSignup' },
      'totalActiveAds': { card: this.acquisitionCards[1], valKey: 'totalActiveAds', trendKey: 'prevWindowActiveAds' },
      'totalPaidSubscriber': { card: this.subscriptionCards[1], valKey: 'newPaidSubscriber', trendKey: 'prevWindowNewPaidSubscriber' }
    };

    if (metricMapping[tile] && typeof data === 'object') {
      const { card, valKey, trendKey } = metricMapping[tile];
      const curVal = Number(data[valKey]);
      const prevWindow = data[trendKey]; // Growth percentage string
      const sign = data['sign'];

      if (!isNaN(curVal)) {
        // Use valKey as the signal key to match what updateAllMetrics expects
        this.currentValues.update(cur => ({ ...cur, [valKey]: curVal }));

        if (prevWindow) {
          card.trend = {
            value: `${Math.abs(parseFloat(prevWindow)).toFixed(1)}%`,
            direction: sign === 'positive' ? 'up' : (sign === 'negative' ? 'down' : 'none'),
            label: 'vs prev period',
            textColorClass: sign === 'positive' ? 'text-emerald-500' : (sign === 'negative' ? 'text-rose-500' : 'text-gray-400')
          };
        }
      }
      return;
    }

    // Individual Metric Mapping for simple numbers
    const metricNames = [
      'totalSignup', 'trialToPaid', 'trialToFreemium',
      'totalActivePaidSubscription', 'newPaidSubscriber',
      'netGrowth', 'cancellations', 'churnRate', 'freeToPaid'
    ];

    if (metricNames.includes(tile)) {
      this.currentValues.update(cur => ({ ...cur, [tile]: Number(data) }));
    }
  }

  private getLabelByUnit(date: string, unit: string): string {
    const d = dayjs(date);
    switch (unit) {
      case 'day':
        return d.format('MMM DD');
      case 'week':
        const start = d.format('MMM DD');
        const end = d.add(6, 'day').format('MMM DD');
        return `${start} - ${end}`;
      case 'month':
        return d.format('MMM');
      case 'quarter':
        return `Q${Math.floor(d.month() / 3) + 1} ${d.format('YY')}`;
      case 'year':
        return d.format('YYYY');
      default:
        return d.format('MMM');
    }
  }

  private mapOnboardingVsProfile(data: any, unit: string): void {
    this.loadingProfileCompletion = false;
    const months = new Set<string>();
    const profileData: any[] = data.BusinessProfileCompleted || [];
    const onboardingData: any[] = data.onboardingCompleted || [];

    profileData.forEach(d => months.add(d.period_from));
    onboardingData.forEach(d => months.add(d.period_from));

    const sortedMonths = Array.from(months).sort();

    this.profileCompletionData = sortedMonths.map(month => {
      const p = profileData.find(d => d.period_from === month);
      const o = onboardingData.find(d => d.period_from === month);
      return {
        label: this.getLabelByUnit(month, unit),
        values: [
          { key: 'profile', value: p ? p.user_count : 0, color: '#c084fc' },
          { key: 'onboarding', value: o ? o.user_count : 0, color: '#10b981' }
        ]
      };
    });
    this.hasProfileCompletionData = this.profileCompletionData.length > 0;
  }

  private mapTrialVsPaidVsFreemium(data: any): void {
    this.loadingConversionTrend = false;
    const trialData: any[] = data.trialUser || data.trailUser || [];
    const paidData: any[] = data.newPaidUser || [];
    const freemiumData: any[] = data.freemiumUser || [];

    this.conversionTrendData[0].data = trialData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.trial_count || 0
    }));

    this.conversionTrendData[1].data = paidData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.business_count || 0
    }));

    this.conversionTrendData[2].data = freemiumData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.freemium_count || 0
    }));

    // Trigger change detection by spreading
    this.conversionTrendData = [...this.conversionTrendData];
    this.hasConversionTrendData = this.conversionTrendData.some(s => s.data.some(d => d.value > 0));
  }

  private mapAutoRenewVsFailure(data: any): void {
    this.loadingRenewalTrend = false;
    const autoRenewData: any[] = data.autoRenew || [];
    const failureData: any[] = data.failure || [];

    this.renewalTrendData[0].data = autoRenewData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.auto_renew_successes || 0
    }));

    this.renewalTrendData[1].data = failureData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.payment_failures || 0
    }));

    this.renewalTrendData = [...this.renewalTrendData];
    this.hasRenewalTrendData = this.renewalTrendData.some(s => s.data.some(d => d.value > 0));
  }

  private mapAllPostVsAiPost(data: any): void {
    this.loadingAiContent = false;
    const totalPostData: any[] = data.totalPost || [];
    const aiPostData: any[] = data.aiPost || [];

    this.aiContentTrendData[0].data = totalPostData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.total_published_post || 0
    }));

    this.aiContentTrendData[1].data = aiPostData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.ai_post || 0
    }));

    this.aiContentTrendData = [...this.aiContentTrendData];
    this.hasAiContentTrendData = this.aiContentTrendData.some(s => s.data.some(d => d.value > 0));
  }

  private mapPostsByPlatforms(data: any): void {
    this.loadingPlatformPosts = false;
    const colors = ['#fca5a5', '#818cf8', '#34d399', '#fbbf24', '#22d3ee', '#c084fc', '#f472b6', '#fb923c', '#94a3b8', '#a3e635'];
    this.userConversionPieData = data
      .filter((d: any) => {
        const name = (d.NAME || d.name || '').toUpperCase();
        return name !== 'TOTAL';
      })
      .map((d: any, i: number) => ({
        label: d.NAME || d.name || `Platform ${i + 1}`,
        value: d.total_posts,
        color: colors[i % colors.length],
        hidePercentage: true
      }));
    this.hasPlatformPostDistributionData = this.userConversionPieData.length > 0;

    const totalItem = data.find((d: any) => {
      const name = (d.NAME || d.name || '').toUpperCase();
      return name === 'TOTAL';
    });
    if (totalItem) {
      this.centerValue = totalItem.total_posts;
    } else {
      // Fallback: sum all individual platform posts
      this.centerValue = this.userConversionPieData.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
    }
  }

  private mapDemoBookedVsRegistration(data: any, unit: string): void {
    this.loadingDemoSignup = false;
    const demoBooked: any[] = data.demoBooked || [];
    const regAfterDemo: any[] = data.regAfterDemo || [];

    this.demoSignupData = demoBooked.map(d => {
      const r = regAfterDemo.find(reg => reg.period_from === d.period_from);
      const registered = r ? r.businesses_registered_after_demo : 0;
      const booked = d.demos_booked || 0;

      return {
        label: this.getLabelByUnit(d.period_from || '', unit),
        bottomValue: registered,
        topValue: Math.max(0, booked - registered)
      };
    });
    this.hasDemoSignupData = this.demoSignupData.length > 0;
  }

  private mapDealCreatedVsDealClaimed(data: any, unit: string): void {
    this.loadingDealRealization = false;
    const dealsCreated: any[] = data.dealsCreated || [];
    const claimedDeals: any[] = data.claimedDeals || [];

    this.dealCreatedData = dealsCreated.map(d => ({
      start: dayjs(d.period_from).format('YYYY-MM-DD'),
      end: dayjs(d.to).format('YYYY-MM-DD'),
      label: this.getLabelByUnit(d.period_from, unit),
      total: d.total_deals_created || 0,
      type: 'bar'
    }));

    this.dealClaimedData = claimedDeals.map(d => ({
      start: dayjs(d.period_from).format('YYYY-MM-DD'),
      end: dayjs(d.to).format('YYYY-MM-DD'),
      label: this.getLabelByUnit(d.period_from, unit),
      total: d.total_claimed_deals || 0,
      type: 'bar'
    }));
    this.hasDealRealizationData = this.dealCreatedData.length > 0 || this.dealClaimedData.length > 0;
  }

  private formatNumber(value: number): string {
    if (value == null) return '0';
    const num = Number(value);
    return Number.isInteger(num)
      ? num.toLocaleString('en-US', { maximumFractionDigits: 0 })
      : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private mapConversionPipeline(data: any, unit: string): void {
    this.loadingConversionPipeline = false;
    const trialToPaid: any[] = data.trialToPaid || [];
    const freeToPaid: any[] = data.freeToPaid || data.trialToFree || [];
    const cancelledSubscription: any[] = data.cancelledSubscription || [];

    const allDates = new Set<string>();
    trialToPaid.forEach(d => allDates.add(d.period || d.period_from));
    freeToPaid.forEach(d => allDates.add(d.period || d.period_from));
    cancelledSubscription.forEach(d => allDates.add(d.period_from || d.period));

    const sortedDates = Array.from(allDates).sort();

    this.conversionPipelineData = sortedDates.map(date => {
      const tp = trialToPaid.find(d => (d.period || d.period_from) === date);
      const fp = freeToPaid.find(d => (d.period || d.period_from) === date);
      const cs = cancelledSubscription.find(d => (d.period_from || d.period) === date);
      return {
        label: this.getLabelByUnit(date, unit),
        values: [
          { key: 'Trial → Paid', value: tp ? tp.trial_to_paid_users : 0, color: '#fcd34d' },
          { key: 'Free → Paid', value: fp ? (fp.free_to_paid_users || fp.trial_to_freemium_users || 0) : 0, color: '#60a5fa' },
          { key: 'Cancellations', value: cs ? cs.cancelled_subscriptions : 0, color: '#f87171' }
        ]
      };
    });
    this.hasConversionPipelineData = this.conversionPipelineData.length > 0;
  }

  private mapRevenueJourney(data: any): void {
    this.loadingFunnel = false;
    const totalSignup = data.totalSignup || 0;
    const profileDone = data.profileDone || 0;
    const onBoardingDone = data.onBoardingDone || 0;
    const trialStarted = data.trialStarted || 0;
    const trialToFreemium = data.trialToFreemium || 0;
    const trialToPaid = data.trialToPaid || 0;

    const safePct = (num: number, denom: number) =>
      denom > 0 ? Math.round((num / denom) * 100) : 0;

    this.signupToRevenueFunnel = [
      { label: 'Signups', value: this.formatNumber(totalSignup), percentage: 100, color: '#2563eb' },
      { label: 'Profile Done', value: this.formatNumber(profileDone), percentage: safePct(profileDone, totalSignup), color: '#3b82f6' },
      { label: 'Onboard Done', value: this.formatNumber(onBoardingDone), percentage: safePct(onBoardingDone, totalSignup), color: '#60a5fa' },
      { label: 'Trial Started', value: this.formatNumber(trialStarted), percentage: safePct(trialStarted, totalSignup), color: '#06b6d4' },
      { label: 'Trial → Freemium', value: this.formatNumber(trialToFreemium), percentage: safePct(trialToFreemium, totalSignup), color: '#f472b6' },
      { label: 'Trial → Paid', value: this.formatNumber(trialToPaid), percentage: safePct(trialToPaid, totalSignup), color: '#c084fc' },
    ];
    this.hasSignupToRevenueFunnelData = totalSignup > 0;
  }

  private mapTotalSignupVsSocialSignups(data: any): void {
    this.loadingSignupTrend = false;
    const businessSignup: any[] = data.businessSignup || [];
    const socialSignup: any[] = data.socialSignup || [];

    this.monthlySignupTrendData[0].data = businessSignup.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.new_business_signups || 0
    }));

    this.monthlySignupTrendData[1].data = socialSignup.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.social_signups || 0
    }));

    this.monthlySignupTrendData = [...this.monthlySignupTrendData];
    this.hasMonthlySignupTrendData = this.monthlySignupTrendData.some(s => s.data.some(d => d.value > 0));
  }

  private mapAdLeadVsActiveAdsVsCampaignsCreated(data: any, unit: string): void {
    this.loadingAdLeads = false;
    const leads: any[] = data.addLead || [];
    const activeAds: any[] = data.activeAds || [];
    const aiAds: any[] = data.aiAdsCreated || [];

    const allDates = new Set<string>();
    leads.forEach(d => allDates.add(d.period_from));
    activeAds.forEach(d => allDates.add(d.period_from));
    aiAds.forEach(d => allDates.add(d.period_from));

    const sortedDates = Array.from(allDates).sort();

    this.adLeadsData = sortedDates.map(date => {
      const l = leads.find(d => d.period_from === date);
      return {
        start: date,
        end: l?.to || date,
        label: this.getLabelByUnit(date, unit),
        value: l ? l.active_leads : 0
      };
    });

    this.activeAdsData = sortedDates.map(date => {
      const a = activeAds.find(d => d.period_from === date);
      return {
        start: date,
        end: a?.to || date,
        label: this.getLabelByUnit(date, unit),
        value: a ? a.active_ads : 0
      };
    });

    this.aiCampaignsData = sortedDates.map(date => {
      const ai = aiAds.find(d => d.period_from === date);
      return {
        start: date,
        end: ai?.to || date,
        label: this.getLabelByUnit(date, unit),
        value: ai ? ai.number_of_ai_ads_created : 0
      };
    });

    this.hasAdLeadsCampaignData = this.adLeadsData.length > 0 || this.activeAdsData.length > 0 || this.aiCampaignsData.length > 0;
  }

  private mapConversion(data: any): void {
    if (data.trialToPaid != null) {
      this.currentValues.update(cur => ({ ...cur, trialToPaid: Number(data.trialToPaid) }));
    }
    if (data.trialToFreemium != null) {
      this.currentValues.update(cur => ({ ...cur, trialToFreemium: Number(data.trialToFreemium) }));
    }
    if (data.freeToPaid != null) {
      this.currentValues.update(cur => ({ ...cur, freeToPaid: Number(data.freeToPaid) }));
    }
  }

  private mapExecutivePerformanceSummary(data: any): void {
    const metricsMap = {
      'Total Signups': 'totalSignups',
      'Total AI Posts': 'totalAiPost',
      'Total Ad Leads': 'adsLeads',
      'Total Ad Spend': 'totalAdsSpend',
      'Total Deals': 'totalDeals',
      'Total Demos Booked': 'totalDemoBooked',
      'Online Revenue': 'dealRevenue',
      'Referral Signups': 'referralSignup'
    };

    this.executivePerformanceMetrics = this.executivePerformanceMetrics.map(m => {
      const apiKeys = metricsMap as any;
      const apiKey = apiKeys[m.label];
      if (apiKey && data[apiKey] != null) {
        let val = data[apiKey];
        let formatted = '0';
        if (m.label.includes('Revenue') || m.label.includes('Spend')) {
          formatted = `$${this.formatNumber(val)}`;
        } else {
          formatted = this.formatNumber(val);
        }
        return { ...m, value: formatted };
      }
      return m;
    });
    this.loadingExecutiveSummary = false;
  }

  private mapAdSpendsByChannel(data: any, unit: string): void {
    this.loadingAdSpend = false;
    const googleData: any[] = data.googleSpend || [];
    const facebookData: any[] = data.facebookSpend || [];
    const instaData: any[] = data.instagramSpend || [];

    this.adSpendTrendData[0].data = googleData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.google_ads_spends || 0
    }));

    this.adSpendTrendData[1].data = facebookData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.facebook_ads_spends || 0
    }));

    this.adSpendTrendData[2].data = instaData.map(d => ({
      date: dayjs(d.period_from).format('YYYY-MM-DD'),
      value: d.instagram_ads_spends || 0
    }));

    this.hasAdSpendData = this.adSpendTrendData.some(s => s.data.length > 0);
  }

  private mapMarketingPerformanceOverview(data: any): void {
    const metrics = [
      { label: 'Total Spend', key: 'totalSpends' },
      { label: 'Cost Per Lead', key: 'costPerLead' },
      { label: 'Google Spend', key: 'googleSpend' },
      { label: 'Facebook Spend', key: 'facebookSpend' },
      { label: 'Leads / Active Ad', key: 'leadsPerActiveAd' },
      { label: 'Deal Claim Rate', key: 'dealClaimedRate' },
      { label: 'Demo → Reg Rate', key: 'demoToRegRate' },
      { label: 'Churn Rate', key: 'churnRate' }
    ];

    this.marketingPerformanceMetrics = metrics.map(m => {
      const val = data[m.key];
      let formatted = '0';
      if (val != null) {
        if (m.label.includes('Rate') || m.label.includes('Reg') || m.label.includes('Metric')) {
          formatted = `${parseFloat(val).toFixed(2)}%`;
        } else if (m.label.includes('Spend')) {
          formatted = `$${this.formatNumber(val)}`;
        } else {
          formatted = val.toString();
        }
      }
      return {
        label: m.label,
        value: formatted,
        valueColorClass: this.marketingPerformanceMetrics.find(dm => dm.label === m.label)?.valueColorClass || 'text-gray-900 font-bold'
      };
    });
    this.loadingMarketingOverview = false;
  }
  acquisitionCards: DashboardCard[] = ACQUISITION_CARDS.map(c => ({ ...c, loading: true }));
  conversionCards: DashboardCard[] = CONVERSION_CARDS.map(c => ({ ...c, loading: true }));
  subscriptionCards: DashboardCard[] = SUBSCRIPTION_CARDS.map(c => ({ ...c, loading: true }));

  monthlySignupTrendData: ILineAreaSeries[] = [...MONTHLY_SIGNUP_TREND_CONFIG];
  profileCompletionData: CylinderChartData[] = [];
  signupToRevenueFunnel: IFunnelDataPoint[] = [];

  conversionTrendData: ILineAreaSeries[] = [...CONVERSION_TREND_CONFIG];
  conversionPipelineData: IsometricBarData[] = [];
  renewalTrendData: ILineAreaSeries[] = [...RENEWAL_TREND_CONFIG];

  aiContentTrendData: ILineAreaSeries[] = [...AI_CONTENT_TREND_CONFIG];

  adLeadsData: BarChartData[] = [];
  activeAdsData: BarChartData[] = [];
  aiCampaignsData: BarChartData[] = [];
  userConversionPieData: any[] = [];

  adSpendTrendData: ILineAreaSeries[] = [...AD_SPEND_TREND_CONFIG];

  demoSignupData: StackedBarData[] = [];
  dealCreatedData: BarChartData[] = [];
  dealClaimedData: BarChartData[] = [];

  marketingPerformanceMetrics = [...MARKETING_PERFORMANCE_METRICS_DEFAULT];
  executivePerformanceMetrics = [...EXECUTIVE_PERFORMANCE_METRICS_DEFAULT];

  readonly sectionHeadingClass =
    'text-[15px] [@media(max-height:620px)]:text-[14px] font-semibold text-gray-800 dark:text-white line-clamp-1 dashboard:line-clamp-none';
  readonly chartHeadingClass =
    'text-[16px] font-bold text-gray-900 dark:text-white mb-1 line-clamp-2 dashboard:line-clamp-none';
  readonly summaryHeadingClass =
    'text-[16px] font-bold text-gray-900 dark:text-white line-clamp-2 dashboard:line-clamp-none';
  readonly cardTitleWrapperClass =
    'flex-1 text-[12px] dashboard:text-[14px] [@media(max-height:700px)]:text-[11px] font-medium text-gray-500 dark:text-gray-300 leading-tight mt-1 [@media(max-height:620px)]:mt-0.5 min-w-0 w-full overflow-hidden';
  readonly cardTitleClass =
    'w-full min-w-0 line-clamp-1 dashboard:line-clamp-none select-none';
  readonly cardValueWrapperClass =
    'text-[20px] [@media(max-height:700px)]:text-[18px] [@media(max-height:620px)]:text-[16px] dashboard:text-[26px] font-semibold dashboard:font-bold text-gray-900 dark:text-white leading-none tracking-tight flex-1 min-w-0 overflow-hidden mb-0.5 [@media(max-height:620px)]:mb-0';
  readonly cardValueClass =
    'w-full min-w-0 truncate dashboard:truncate-none select-none';
  readonly cardTrendValueClass =
    'flex items-center text-[11px] [@media(max-height:620px)]:text-[10px] dashboard:text-[13px] font-semibold dashboard:font-bold leading-none mb-1 [@media(max-height:620px)]:mb-0.5';
  readonly cardTrendLabelClass =
    'text-[10px] dashboard:text-[11px] [@media(max-height:620px)]:text-[9px] leading-none text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap';

  isClipped(element: HTMLElement | null): boolean {
    if (!element) return false;
    return element.offsetWidth < element.scrollWidth || element.offsetHeight < element.scrollHeight;
  }
}
